<template>
    <div class="space-y-2 pb-5">
        <!-- Bank identifiers stay text values so leading zeroes are never discarded. -->
        <div>
            <field
                v-slot="{ componentField, errorMessage }"
                name="routingNumber"
            >
                <div class="font-medium text-gray-800">
                    {{ t('checkoutSections.bankRoutingNumber') }}
                </div>
                <input-text
                    v-bind="componentField"
                    class="w-full"
                    :placeholder="t('checkoutSections.routingNumber')"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    maxlength="9"
                    :disabled="isProcessing"
                    :pt="{
                        root: {
                            class: errorMessage
                                ? 'border-red-500 hover:border-red-300 focus:shadow-[0_0_0_0.2rem_rgba(254,202,202,1)]'
                                : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                />
                <div
                    v-if="errorMessage"
                    class="text-red-600 pt-1 text-sm font-medium"
                >
                    {{ errorMessage }}
                </div>
            </field>
        </div>

        <div>
            <field
                v-slot="{ componentField, errorMessage }"
                name="accountNumber"
            >
                <div class="font-medium text-gray-800">
                    {{ t('checkoutSections.accountNumber') }}
                </div>
                <input-text
                    v-bind="componentField"
                    class="w-full"
                    :placeholder="t('checkoutSections.accountNumber')"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    :disabled="isProcessing"
                    maxlength="50"
                    :pt="{
                        root: {
                            class: errorMessage
                                ? 'border-red-500 hover:border-red-300 focus:shadow-[0_0_0_0.2rem_rgba(254,202,202,1)]'
                                : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                />
                <div
                    v-if="errorMessage"
                    class="text-red-600 pt-1 text-sm font-medium"
                >
                    {{ errorMessage }}
                </div>
            </field>
        </div>

        <div>
            <field
                v-slot="{ componentField, errorMessage }"
                name="accountNumberConfirm"
                :validate-on-model-update="false"
            >
                <div class="font-medium text-gray-800">
                    {{ t('checkoutSections.confirmAccountNumber') }}
                </div>
                <input-text
                    v-bind="componentField"
                    class="w-full"
                    :placeholder="t('checkoutSections.confirmAccountNumber')"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    :disabled="isProcessing"
                    maxlength="50"
                    :pt="{
                        root: {
                            class: errorMessage
                                ? 'border-red-500 hover:border-red-300 focus:shadow-[0_0_0_0.2rem_rgba(254,202,202,1)]'
                                : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                />
                <div
                    v-if="errorMessage"
                    class="text-red-600 pt-1 text-sm font-medium"
                >
                    {{ errorMessage }}
                </div>
            </field>
        </div>

        <div>
            <field
                v-slot="{ componentField, errorMessage }"
                name="accountType"
            >
                <div class="font-medium text-gray-800">
                    {{ t('checkoutSections.accountType') }}
                </div>
                <div class="flex flex-col gap-1 pt-1">
                    <div class="flex gap-2 pl-2">
                        <radio-button
                            v-bind="componentField"
                            input-id="checkingAccount"
                            value="CHECKING"
                            :disabled="isProcessing"
                            :pt="{
                                box: {
                                    class: errorMessage
                                        ? 'border-red-500 hover:border-red-300 focus:shadow-[0_0_0_0.2rem_rgba(254,202,202,1)]'
                                        : ''
                                }
                            }"
                            :pt-options="{
                                mergeSections: true,
                                mergeProps: true
                            }"
                        />
                        <label
                            for="checkingAccount"
                            class="text-gray-600"
                            >{{ t('checkoutSections.checking') }}</label
                        >
                    </div>
                    <div class="flex gap-2 pl-2">
                        <radio-button
                            v-bind="componentField"
                            input-id="savingsAccount"
                            value="SAVINGS"
                            :disabled="isProcessing"
                            :pt="{
                                box: {
                                    class: errorMessage
                                        ? 'border-red-500 hover:border-red-300 focus:shadow-[0_0_0_0.2rem_rgba(254,202,202,1)]'
                                        : ''
                                }
                            }"
                            :pt-options="{
                                mergeSections: true,
                                mergeProps: true
                            }"
                        />
                        <label
                            for="savingsAccount"
                            class="text-gray-600"
                            >{{ t('checkoutSections.savings') }}</label
                        >
                    </div>
                    <div
                        v-if="errorMessage"
                        class="text-red-600 text-sm font-medium"
                    >
                        {{ errorMessage }}
                    </div>
                </div>
            </field>
        </div>
    </div>
</template>

<script setup lang="ts">
import RadioButton from 'primevue/radiobutton'
import InputText from 'primevue/inputtext'
import { toTypedSchema } from '@vee-validate/yup'
import { mixed, object, string } from 'yup'
import { Field, useForm } from 'vee-validate'
import type { BankAccountPaymentMethod } from '~/components/Checkout/Sections/Payment/payment-method'
import { useCheckoutFormProcessing } from '~/components/Checkout/Forms/use-checkout-form'
import { useI18n } from 'vue-i18n'

const emit = defineEmits<{
    (e: 'paymentMethod', payload: BankAccountPaymentMethod): void
}>()

const { t } = useI18n({ useScope: 'global' })

// Match CardSecure's numeric ACH payload requirements before checkout can be submitted.
const bankAccountSchema = toTypedSchema(
    object({
        routingNumber: string()
            .trim()
            .required(t('checkoutSections.bankRoutingNumberRequired'))
            .matches(/^[0-9]{9}$/, {
                excludeEmptyString: true,
                message: t('checkoutSections.bankRoutingNumberInvalid')
            }),
        accountNumber: string()
            .trim()
            .required(t('checkoutSections.bankAccountNumberRequired'))
            .matches(/^[0-9]+$/, {
                excludeEmptyString: true,
                message: t('checkoutSections.bankAccountNumberInvalid')
            }),
        accountNumberConfirm: string()
            .trim()
            .required(t('checkoutSections.confirmBankAccountNumberRequired'))
            .test({
                name: 'ConfirmAccountNumber',
                exclusive: false,
                message: t('checkoutSections.bankAccountNumbersMismatch'),
                test: function (value, context) {
                    return value === context.parent.accountNumber
                }
            }),
        accountType: mixed()
            .oneOf(
                ['CHECKING', 'SAVINGS'],
                t('checkoutSections.bankAccountTypeInvalid')
            )
            .required(t('checkoutSections.bankAccountTypeRequired'))
    })
)

const { isProcessing } = useCheckoutFormProcessing()

const isDev = import.meta.env.DEV
const { handleSubmit } = useForm({
    validationSchema: bankAccountSchema,
    initialValues: {
        routingNumber: isDev ? '036001808' : '',
        accountNumber: isDev ? '123456789' : '',
        accountNumberConfirm: isDev ? '123456789' : '',
        accountType: ''
    }
})

const onSubmit = handleSubmit(values => {
    emit('paymentMethod', {
        type: 'BankAccountPaymentMethod',
        routingNumber: values.routingNumber,
        accountNumber: values.accountNumber,
        accountNumberConfirm: values.accountNumberConfirm,
        accountType:
            values.accountType as BankAccountPaymentMethod['accountType']
    })
})

async function submitBankAccountForm() {
    await onSubmit()
}

defineExpose({
    submitBankAccountForm
})
</script>
